import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är en storyboard?",
    a: "En storyboard (bildmanus) är en serie skisser eller bilder som visar filmen ruta för ruta innan den spelas in. Varje ruta motsvarar en bild eller ett skeende och kompletteras med bildutsnitt, kamerarörelse, längd och replik eller speakertext.",
  },
  {
    q: "Vad är skillnaden mellan storyboard och bildmanus?",
    a: "Ingen — bildmanus är den svenska termen för storyboard. I produktionssammanhang används orden omväxlande. Ett animatic är nästa steg: storyboarden klippt till rätt längd med speaker och temp-musik.",
  },
  {
    q: "Behöver alla filmer en storyboard?",
    a: "Nej. Intervjubaserad företagsfilm och dokumentärt material klarar sig med scenlista och shotlist. Storyboard lönar sig när varje sekund kostar: reklamfilm, animation, produktfilm med kamerakoreografi och allt som ska godkännas av flera intressenter innan inspelning.",
  },
  {
    q: "Vad kostar en storyboard?",
    a: "En enkel skiss-storyboard på 12–20 rutor ligger på 6 000–15 000 kr. En detaljerad storyboard för reklamfilm med 30–40 rutor, kamerarörelser och färglagda nyckelbilder: 18 000–35 000 kr. Animatic med ljud tillkommer med 8 000–20 000 kr.",
  },
  {
    q: "Kan man använda en mall för storyboard?",
    a: "Ja, för enkla filmer räcker en mall med rutor för bild, bildutsnitt, kamerarörelse, längd och ljud. Mallen löser dock inte det svåra: att veta vilka bilder som faktiskt bär berättelsen och går att spela in inom budget.",
  },
  {
    q: "Hur många rutor behöver en 30-sekundersfilm?",
    a: "Räkna med ungefär en ruta per bild. En reklamfilm på 30 sekunder har ofta 12–20 klipp, alltså 12–20 rutor. Snabbklippta filmer kan landa på 30 rutor, medan en film med långa tagningar klarar sig på 6–8.",
  },
];

const TITLE = "Bildmanus — hur många rutor behöver en 30-sekundersfilm?";
const DESCRIPTION =
  "Se ett riktigt bildmanus ruta för ruta, hur mallen ser ut och när bildmanuset sparar mer än det kostar. Plus skillnaden mot manus, förklarad på en minut.";

export const Route = createFileRoute("/storyboard")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Bildmanus — hur många rutor behöver en 30-sekundersfilm?" },
      { property: "og:description", content: "Bildmanus ruta för ruta — mall, antal rutor och när det lönar sig." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://fenny.se/storyboard" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/storyboard" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: "/storyboard", name: TITLE, description: DESCRIPTION })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Storyboard och bildmanus",
            path: "/storyboard",
            serviceType: "Storyboard",
            description: "Storyboard och bildmanus för reklamfilm, företagsfilm och animation — skiss, detaljerad board eller animatic.",
            priceRange: "6 000–35 000 kr",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Manus och koncept", path: "/manus-och-koncept" },
            { name: "Storyboard", path: "/storyboard" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <LandingPage
        eyebrow="Storyboard"
        title={<>Storyboard — se filmen innan ni betalar för den.</>}
        lede="En storyboard (bildmanus) visar filmen ruta för ruta innan inspelning: bildutsnitt, kamerarörelse, rytm och ljud. Det är det billigaste sättet att upptäcka att en idé inte håller — och det snabbaste sättet att få hela beslutskedjan att se samma film."
        tldr={
          <p>
            En storyboard är en visuell plan där varje ruta motsvarar en bild i
            filmen. Pris i Sverige 2026: <strong>6 000–15 000 kr</strong> för
            skiss-storyboard (12–20 rutor), <strong>18 000–35 000 kr</strong> för
            detaljerad reklamfilms-board. Tumregel: en 30-sekundersfilm har
            typiskt <strong>12–20 rutor</strong>. Bildmanus är samma sak på
            svenska.
          </p>
        }
        ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
        ctaSecondary={{ label: "Manus och koncept", to: "/manus-och-koncept" }}
        trackingId="storyboard"
        bullets={[
          { title: "Billigare än ett omtag", body: "Ändringar i en ruta kostar minuter. Ändringar på inspelningsdagen kostar tiotusentals kronor." },
          { title: "Ett gemensamt facit", body: "Alla intressenter godkänner samma bild av filmen — innan produktion." },
          { title: "Skiss till animatic", body: "Från snabba streckskisser till färglagd board eller animatic med speaker." },
        ]}
        pricing={{
          heading: "Vad kostar en storyboard?",
          intro: "Riktpriser exklusive moms, Sverige 2026.",
          rows: [
            { format: "Skiss-storyboard", scope: "12–20 rutor, streckskiss, bildutsnitt och ljudnot", price: "6 000–15 000 kr", lead: "3–5 dagar" },
            { format: "Detaljerad board", scope: "20–40 rutor, kamerarörelser, nyckelbilder i färg", price: "18 000–35 000 kr", lead: "1–2 veckor" },
            { format: "Animatic", scope: "Boarden klippt till längd med speaker och temp-musik", price: "+8 000–20 000 kr", lead: "3–5 dagar" },
            { format: "Storyboard för animation", scope: "Board + karaktärs- och layoutunderlag", price: "25 000–60 000 kr", lead: "2–3 veckor" },
          ],
          footnote: "Storyboard ingår i konceptarvodet när vi också producerar filmen som Prime-produktion.",
        }}
        process={{
          heading: "Så tas en storyboard fram",
          steps: [
            { label: "Manus och scenlista", body: "Boarden utgår alltid från ett godkänt manus — annars ritar vi gissningar.", duration: "innan start" },
            { label: "Thumbnails", body: "Snabba miniatyrer där vi testar bildföljd och rytm utan att lägga tid på detaljer.", duration: "dag 1–2" },
            { label: "Board", body: "Rutorna ritas rent med bildutsnitt, kamerarörelse, längd och ljud per ruta.", duration: "dag 3–7" },
            { label: "Genomgång", body: "Vi går igenom boarden med er och justerar innan den blir produktionsunderlag.", duration: "vecka 2" },
            { label: "Animatic vid behov", body: "Boarden klipps till rätt längd med speaker så tajmningen kan godkännas.", duration: "vecka 2" },
          ],
        }}
        sections={[
          {
            id: "vad-star-i-en-ruta",
            heading: "Vad varje ruta ska innehålla",
            body: (
              <>
                <p>
                  Bild (vad ser vi), bildutsnitt (helbild, halvbild, närbild),
                  kamerarörelse (statisk, panorering, åkning), längd i sekunder,
                  samt ljud — replik, speakertext, effekt eller musikmarkering.
                </p>
                <p>
                  Det räcker med streckgubbar. Konstnärlig kvalitet i skisserna är
                  irrelevant; det som räknas är att bildföljden går att läsa och
                  att varje ruta faktiskt går att spela in inom budget.
                </p>
              </>
            ),
          },
          {
            id: "nar-lonar-det-sig",
            heading: "När storyboard lönar sig — och när det inte gör det",
            body: (
              <>
                <p>
                  Lönar sig: <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">reklamfilm</a>{" "}
                  med korta klipp och hög sekundkostnad,{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/animerad-forklaringsfilm">animerad förklaringsfilm</a>{" "}
                  där varje ruta ändå måste ritas, produktfilm med kamerakoreografi
                  och allt som ska godkännas av flera avdelningar.
                </p>
                <p>
                  Lönar sig sällan: intervjubaserad{" "}
                  <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foretagsfilm">företagsfilm</a>,
                  eventdokumentation och dokumentärt material — där ger en
                  shotlist mer nytta per krona.
                </p>
              </>
            ),
          },
          {
            id: "mall",
            heading: "Storyboard-mall",
            body: (
              <p>
                En användbar mall har fem kolumner: rutnummer, bildruta,
                bildutsnitt och kamerarörelse, längd, samt ljud. Skriv alltid
                total längd längst ned — det är den vanligaste anledningen till
                att en board faller: filmen är 48 sekunder när briefen sa 30.
                Behöver ni hjälp att gå från manus till board tar vi det som
                fristående uppdrag.
              </p>
            ),
          },
        ]}
        faq={FAQ}
        caseLimit={4}
        riskReducers
        closing={{
          title: "Ska en idé bli bild?",
          body: "Skicka manus eller brief så säger vi vilken nivå av board ni faktiskt behöver.",
          primary: { label: "Boka strategimöte", to: "/kontakt" },
        }}
      />
      <RelatedKnowledgeAuto topics={["manus", "reklamfilm", "animation", "process"]} />
    </>
  );
}
